import {useQueries, useQuery, useSuspenseQueries, useSuspenseQuery} from '@tanstack/react-query';
import {find, some} from 'lodash';
import {ComponentProps, FC, useCallback, useLayoutEffect} from 'react';
import {
  RouterProvider, Navigate, createHashRouter,
  useRouteError, useParams, useSearchParams,
} from 'react-router-dom';
import {FetchDataError, Loader, interpolateRoute, request} from 'apstra-ui-common';

import IBAContext from '../IBAContext';
import {withSuspenseWrapper} from '../../SuspenseWrapper';
import {allowResponseErrorCode} from '../../allowResponseErrorCode';
import {apiQueryOptions} from '../../apiQueryOptions';
import ProbeDetails, {probeDetailsPollingInterval, ProbeDetailsProps} from './ProbeDetails';
import ProbeList from './ProbeList';
import AnomalyList from './AnomalyList';
import ReportList from './report/ReportList';
import ReportContainer from './report/ReportContainer';
import DashboardList from './DashboardList';
import DashboardDetails from './DashboardDetails';
import AutoEnabledDashboards from './AutoEnabledDashboards';
import useUserStore from '../../hooks/useUserStore';

import './IBA.less';

export const routes = {
  probeList: '/api/blueprints/<blueprint_id>/probes',
  probeDetails: '/api/blueprints/<blueprint_id>/probes/<probe_id>',
  probeStage: '/api/blueprints/<blueprint_id>/probes/<probe_id>/query',
  probesBatchDelete: '/api/blueprints/<blueprint_id>/probes-batch-delete',
  cablingMap: '/api/blueprints/<blueprint_id>/experience/web/cabling-map',
  dashboardList: '/api/blueprints/<blueprint_id>/iba/dashboards',
  dashboardDetails: '/api/blueprints/<blueprint_id>/iba/dashboards/<dashboard_id>',
  dashboardsBatchDelete: '/api/blueprints/<blueprint_id>/iba/dashboards-batch-delete',
  dashboardExport: '/api/blueprints/<blueprint_id>/iba/dashboards/<dashboard_id>/export',
  dashboardImport: '/api/blueprints/<blueprint_id>/iba/dashboards/import',
  predefinedProbeList: '/api/blueprints/<blueprint_id>/iba/predefined-probes',
  predefinedProbeDetails: '/api/blueprints/<blueprint_id>/iba/predefined-probes/<predefined_probe>',
  predefinedProbeParameters: '/api/blueprints/<blueprint_id>/iba/predefined-probes/<predefined_probe>/<probe_id>',
  predefinedDashboard: '/api/blueprints/<blueprint_id>/iba/predefined-dashboards/<predefined_dashboard>',
  predefinedDashboardList: '/api/blueprints/<blueprint_id>/iba/predefined-dashboards',
  anomalyList: '/api/blueprints/<blueprint_id>/anomalies',
  anomalousStageList: '/api/blueprints/<blueprint_id>/iba/anomalous-stages',
  vShpereAnomalyResolver: (
    '/api/blueprints/<blueprint_id>/virtual_infra/predefined_probes/virtual_infra_vlan_match/anomaly_resolver'
  ),
  telemetryServiceWarnings: '/api/blueprints/<blueprint_id>/iba/probes/<probe_id>/ts-warnings/<stage_name>',
  telemetryServiceRegistry: '/api/telemetry-service-registry',
  diskSpaceUsageForProbe: '/api/metricdb/metric/iba/<blueprint_id>/<probe_id>/stats',
  diskSpaceUsageForStage: '/api/metricdb/metric/iba/<blueprint_id>/<probe_id>/<stage_name>/stats',
  predefinedProbeReportList: '/api/blueprints/<blueprint_id>/predefined-reports',
  predefinedProbeReportRegistry: '/api/blueprints/<blueprint_id>/predefined-reports/<report_name>',
  predefinedProbeReportQuery: '/api/blueprints/<blueprint_id>/predefined-report/<report_name>',
  referenceDesignSchemaDetails: '/api/docs/reference_design_schemas/<reference_design_name>',
};

const DashboardDetailsWithFetchData: FC = withSuspenseWrapper(() => {
  const {blueprintId, dashboardId, action} = useParams();
  const {
    dashboard, probes, predefinedDashboards
  } = useSuspenseQueries({
    queries: [{
      ...apiQueryOptions.dashboardDetails(blueprintId, dashboardId),
      queryFn: ({signal}) => request(interpolateRoute(routes.dashboardDetails, {blueprintId, dashboardId}), {signal}),
    }, {
      ...apiQueryOptions.blueprintProbes(blueprintId),
      queryFn: ({signal}) => request(interpolateRoute(routes.probeList, {blueprintId}), {signal}),
    }, {
      ...apiQueryOptions.blueprintPredefinedDashboards(blueprintId),
      queryFn: ({signal}) => request(interpolateRoute(routes.predefinedDashboardList, {blueprintId}), {signal}),
    }],
    combine: (results) => ({
      dashboard: results[0].data,
      probes: results[1].data?.items,
      predefinedDashboards: results[2].data?.items,
    }),
  });

  return <DashboardDetails
    key={`${action} ${dashboardId}`}
    action={action}
    dashboard={dashboard}
    dashboardId={dashboardId}
    predefinedDashboards={predefinedDashboards}
    probes={probes}
  />;
});

const CreateDashboardWithFetchData: FC = withSuspenseWrapper(() => {
  const {blueprintId} = useParams();
  const {
    probes, predefinedDashboards
  } = useSuspenseQueries({
    queries: [{
      ...apiQueryOptions.blueprintProbes(blueprintId),
      queryFn: ({signal}) => request(interpolateRoute(routes.probeList, {blueprintId}), {signal}),
    }, {
      ...apiQueryOptions.blueprintPredefinedDashboards(blueprintId),
      queryFn: ({signal}) => request(interpolateRoute(routes.predefinedDashboardList, {blueprintId}), {signal}),
    }],
    combine: (results) => ({
      probes: results[0].data?.items,
      predefinedDashboards: results[1].data?.items,
    }),
  });
  return <DashboardDetails
    action='create'
    predefinedDashboards={predefinedDashboards}
    probes={probes}
  />;
});

const AnomalyListWrapper: FC<ComponentProps<typeof AnomalyList>> = (props) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [preferences, setPreferences] = useUserStore('anomaly list grouped');
  useLayoutEffect(() => {
    if (preferences?.grouped === 'false') {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set('grouped', 'false');
        return newParams;
      });
    }
  }, [preferences?.grouped, setSearchParams]);
  const grouped = (searchParams.get('grouped') !== 'false');
  const toggleGrouped = useCallback(() => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      if (prev.get('grouped') !== 'false') {
        setPreferences({grouped: 'false'});
        newParams.set('grouped', 'false');
      } else {
        setPreferences({grouped: 'true'});
        newParams.delete('grouped');
      }
      return newParams;
    });
  }, [setPreferences, setSearchParams]);
  return (
    <AnomalyList
      grouped={grouped}
      toggleGrouped={toggleGrouped}
      {...props}
    />
  );
};

const ProbeDetailsWithFetchData: FC = withSuspenseWrapper(() => {
  const {blueprintId, probeId, action} = useParams();
  const {
    probe, telemetryServiceRegistryItems, refetch
  } = useSuspenseQueries({
    queries: [{
      ...apiQueryOptions.telemetryServiceRegistry(),
      queryFn: ({signal}) => request(routes.telemetryServiceRegistry, {signal}),
    }, {
      ...apiQueryOptions.probeDetails(blueprintId, probeId),
      queryFn: ({signal}) => request(interpolateRoute(routes.probeDetails, {blueprintId, probeId}), {signal}),
      refetchInterval: action ? false : probeDetailsPollingInterval,
    }],
    combine: (results) => ({
      telemetryServiceRegistryItems: results[0].data?.items,
      probe: results[1].data,
      refetch: () => Promise.all([results[0].refetch(), results[1].refetch()]),
    }),
  });

  const {data, isLoading, error} = useQuery({
    ...apiQueryOptions.diskSpaceUsageForProbe(blueprintId, probeId),
    queryFn: ({signal}) => allowResponseErrorCode(
      request(interpolateRoute(routes.diskSpaceUsageForProbe, {blueprintId, probeId}), {signal}),
      404
    ),
    enabled: some(probe?.stages, {enable_metric_logging: true}),
    refetchInterval: action ? false : probeDetailsPollingInterval,
  });

  return isLoading ? <Loader />
    : error ? <FetchDataError error={error} />
    : (
      <ProbeDetails
        key={`${action} ${probeId}`}
        action={action as ProbeDetailsProps['action']}
        diskUsage={data as ProbeDetailsProps['diskUsage']}
        probe={probe}
        probeId={probeId}
        refetchData={() => refetch()}
        telemetryServiceRegistryItems={telemetryServiceRegistryItems}
      />
  );
});

const CreateProbeWithFetchData = withSuspenseWrapper(() => {
  const {data, refetch} = useSuspenseQuery({
    ...apiQueryOptions.telemetryServiceRegistry(),
    queryFn: ({signal}) => request(routes.telemetryServiceRegistry, {signal}),
  });
  return <ProbeDetails
    action='create'
    telemetryServiceRegistryItems={(data as any)?.items}
    refetchData={() => refetch()}
  />;
});

export const RouterErrorBoundary = () => {
  const error = useRouteError();
  return <FetchDataError error={error} />;
};

const router = createHashRouter([
  {
    // this will be briefly shown when navigating away from the Analytics tab
    path: '*',
    element: <></>,
    errorElement: <></>,
  },
  {
    path: '/blueprints/:blueprintId/analytics',
    ErrorBoundary: RouterErrorBoundary,
    children: [
      {
        path: 'dashboards',
        children: [
          {
            path: ':dashboardId/:action?',
            Component: DashboardDetailsWithFetchData,
          },
          {
            index: true,
            Component: DashboardList,
          }
        ],
      },
      {
        path: 'auto-enabled-dashboards',
        Component: AutoEnabledDashboards,
      },
      {
        path: 'create-dashboard',
        Component: CreateDashboardWithFetchData,
      },
      {
        path: 'anomalies',
        Component: AnomalyListWrapper,
      },
      {
        path: 'probes',
        children: [
          {
            path: 'create-probe',
            Component: CreateProbeWithFetchData,
          },
          {
            path: ':probeId',
            children: [
              {
                path: 'stages/:stage/:action?',
                Component: ProbeDetailsWithFetchData,
              },
              {
                path: 'processors/:processor/:action?',
                Component: ProbeDetailsWithFetchData,
              },
              {
                path: ':action',
                Component: ProbeDetailsWithFetchData,
              },
              {
                index: true,
                Component: ProbeDetailsWithFetchData,
              }
            ],
          },
          {
            index: true,
            Component: ProbeList,
          },
        ],
      },
      {
        path: 'reports',
        children: [
          {
            path: ':reportName',
            Component: ReportContainer,
          },
          {
            index: true,
            Component: ReportList,
          }
        ],
      },
      {
        index: true,
        element: <Navigate to='dashboards' replace />,
      },
    ],
  },
]);

// FIXME(vkramskikh): this is a hack to connect react-router (JS) with bide (CLJS).
// bide doesn't use html5 history API in the hash mode and listens to hashchange event.
// This hack should be removed after switching to html5 history API everywhere.
const originalNavigate = router.navigate;
router.navigate = function(...args) {
  const result = originalNavigate.apply(router, args as any);
  window.dispatchEvent(new HashChangeEvent('hashchange'));
  return result;
};

const IBA = (props) => {
  const {
    blueprintId, blueprintDesign, allActiveNodes, systemIdMap, systemsHrefs, vnIdMap, cablingMap,
    processorDefinitions, blueprintPermissions, anomalyRemediationUrl, generateVnLink, blueprintTags, isFreeform,
    deviceProfiles, processorCategories,
  } = props;
  const {
    isLoading, fetchDataError, predefinedProbes, blueprintReferenceDesignSchema
  } = useQueries({
    queries: [{
      ...apiQueryOptions.predefinedProbes(blueprintId),
      queryFn: ({signal}) => request(
        interpolateRoute(routes.predefinedProbeList, {blueprintId}),
        {signal, queryParams: {include_experimental: true}}
      ),
    }, {
      ...apiQueryOptions.blueprintReferenceDesign(blueprintDesign),
      queryFn: ({signal}) => request(
        interpolateRoute(routes.referenceDesignSchemaDetails, {referenceDesignName: blueprintDesign}),
        {signal}
      ),
      enabled: !!blueprintDesign,
    }],
    combine: (results) => ({
      isLoading: some(results, (r) => r.isLoading),
      fetchDataError: find(results, (r) => !!r.error)?.error,
      predefinedProbes: results[0].data?.items,
      blueprintReferenceDesignSchema: results[1].data ?? null
    })
  });

  return (
    <IBAContext.Provider
      value={{
        blueprintId, blueprintDesign, allActiveNodes, systemIdMap, systemsHrefs, vnIdMap, cablingMap,
        processorDefinitions, predefinedProbes, blueprintReferenceDesignSchema,
        routes, blueprintPermissions, anomalyRemediationUrl,
        generateVnLink, blueprintTags, isFreeform, deviceProfiles, processorCategories,
      }}
    >
      <div className='iba-root'>
        {isLoading ?
          <Loader />
        : fetchDataError ?
          <FetchDataError error={fetchDataError} />
        :
          <RouterProvider router={router} />
        }
      </div>
    </IBAContext.Provider>
  );
};

export default IBA;
